/**
 * Product Gallery JavaScript
 * Handles product image gallery with variant-specific media updates
 * Converted from TypeScript to match exact functionality
 */
// @ts-nocheck
class ProductGallery {
  constructor(element) {
    this.host = element;
    this.sectionContainerEl = this.host.closest('.product-details');
    this.lightboxGalleryEl = this.sectionContainerEl?.querySelector('[lightbox-gallery]');
    this.scriptEl = this.host.querySelector('#ProductGalleryData');
    this.data = null;
    this.instance = null;
    this.thumbsInstance = null;
    this.templateSuffix = this.host.dataset?.templateSuffix || '';

    this.defaultThumbs = [];
    this.dimensionThumb = null;
    this.defaultSlides = [];
    this.dimensionSlide = null;
    this.defaultLightboxSlides = [];
    this.defaultLightboxThumbs = [];

    this.init();
  }

  init() {
    try {
      this.data = JSON.parse(this.scriptEl.textContent);
    } catch (err) {
      console.error("Error parsing ProductGallery data: ", err);
      return;
    }

    this.templateSuffix = this.host.dataset?.templateSuffix || '';
    this.initSwiper();
  }

  finishInit() {
    if (!this.instance) return;

    if (this.lightboxGalleryEl) {
      this.initLightboxGallery();
    }

    if (this.instance.thumbs?.swiper) {
      this.defaultThumbs = [...Array.from(this.instance.thumbs.swiper.slides)];
      this.dimensionThumb = this.defaultThumbs.find(s => s.hasAttribute('gallery-media-dimension'));
    }

    this.defaultSlides = [...Array.from(this.instance.slides)];
    this.dimensionSlide = this.defaultSlides.find(s => s.hasAttribute('gallery-media-dimension'));

    this.bindEvents();

    if (this.lightboxGallery?.instance) {
      this.lightboxGallery.instance.update?.();
    }
    this.instance.update();

    this.applyInitialVariantMedia();
    this.dispatchGalleryUpdated();
  }

  applyInitialVariantMedia() {
    if (!this.instance || !this.data?.variantGalleries) return;

    // Determine the currently selected variant ID
    let variantId = null;

    // 1. Try URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) {
      variantId = parseInt(urlVariant);
    }

    // 2. Try variant-picker's current variant
    if (!variantId) {
      const variantPicker = document.querySelector('variant-picker');
      if (variantPicker && variantPicker.currentVariant) {
        variantId = variantPicker.currentVariant.id;
      }
    }

    // 3. Fall back to defaultVariantId from ProductGalleryData
    if (!variantId && this.data.defaultVariantId) {
      variantId = this.data.defaultVariantId;
    }

    if (!variantId) return;

    // Check if this variant has media in variantGalleries
    const mediaEntry = Object.entries(this.data.variantGalleries).find(([key]) => +key === variantId);
    const newMedia = mediaEntry?.[1] || null;

    // CRITICAL FIX: Always update for initial variant, even if media is empty
    // This ensures lightbox gets properly initialized with correct slides
    // Empty media will trigger revertToDefaultMedia() which shows featured_media
    if (newMedia && newMedia.length > 0 && this.currentSlidesMatchMedia(newMedia)) {
      // Regression: Keep the Liquid LCP <img> when it already matches this variant's gallery.
      this._lastProcessedVariantId = variantId;
      return;
    }
    
    // Always call updateShownVariantMedia for initial load to ensure lightbox sync
    this.updateShownVariantMedia(variantId);
  }

  // Regression
  currentSlidesMatchMedia(newMedia) {
    if (!this.instance || !newMedia?.length) return false;

    const currentIds = Array.from(this.instance.slides || [])
      .filter((slide) => !slide.hasAttribute('gallery-media-dimension'))
      .map((slide) => String(slide.getAttribute('data-thumbnail-id') || ''));
    const newIds = newMedia.map((media) => String(media?.id || ''));

    if (!currentIds.length || currentIds.length !== newIds.length) return false;
    return currentIds.every((id, index) => id && id === newIds[index]);
  }

  initLightboxGallery() {
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds max (50 * 100ms)
    
    // Try to get LightboxGallery instance - may not be initialized yet
    const tryInit = () => {
      retryCount++;
      
      let swiperInstance = null;
      
      // Path 1: Try lightboxGalleryInstance
      if (this.lightboxGalleryEl.lightboxGalleryInstance) {
        this.lightboxGallery = this.lightboxGalleryEl.lightboxGalleryInstance;
        const lightboxCarousel = this.lightboxGalleryEl.querySelector('[carousel]');
        if (lightboxCarousel && lightboxCarousel.swiper) {
          swiperInstance = lightboxCarousel.swiper;
        }
      }
      
      // Path 2: Fallback - try direct carousel lookup
      if (!swiperInstance) {
        const lightboxCarousel = this.lightboxGalleryEl.querySelector('[carousel]');
        if (lightboxCarousel && lightboxCarousel.swiper) {
          swiperInstance = lightboxCarousel.swiper;
          this.lightboxGallery = { instance: swiperInstance };
        }
      }
      
      // If we found swiper, complete initialization
      if (swiperInstance) {
        this.lightboxGallery.instance = swiperInstance;
        this.defaultLightboxSlides = Array.from(swiperInstance.slides || []);
        this.defaultLightboxThumbs = Array.from(swiperInstance.thumbs?.swiper?.slides || []);
        this.syncGalleries();
        // CRITICAL FIX: Don't apply pending media during init - defer until first click
        // This prevents breaking the lightbox's open functionality
      } else if (retryCount < maxRetries) {
        setTimeout(tryInit, 100);
      }
    };
    
    // Start trying to initialize
    tryInit();
  }

  applyPendingLightboxMedia() {
    // Check if there's pending media to apply
    if (this._pendingLightboxMedia && this.lightboxGallery?.instance) {
      const pendingMedia = this._pendingLightboxMedia;
      this._pendingLightboxMedia = null; // Clear pending media
      
      // Now apply the media (lightbox is ready now)
      this.unbindLightboxEvents();
      this.disableAndClearLightboxSwipers();

      const slides = this.SlideElements(pendingMedia, 'lightbox');
      const thumbs = this.SlideElements(pendingMedia, 'lightbox-thumb');

      const slideElements = slides.map(html => this.htmlToElement(html));
      const thumbElements = thumbs.map(html => this.htmlToElement(html));

      if (this.lightboxGallery.instance.thumbs?.swiper) {
        this.lightboxGallery.instance.thumbs.swiper.appendSlide(thumbElements);
      }
      this.lightboxGallery.instance.appendSlide(slideElements);

      if (this.dimensionSlide && this.dimensionThumb) {
        const insertIndex = (this.templateSuffix === 'furniture_new') ? 2 : 1;
        this.lightboxGallery.instance.addSlide(insertIndex, this.dimensionSlide);
        if (this.lightboxGallery.instance.thumbs?.swiper) {
          this.lightboxGallery.instance.thumbs.swiper.addSlide(insertIndex, this.dimensionThumb);
        }
      }

      this.enableAndUpdateLightboxSwipers();
    }
  }

  bindEvents() {
    if (!this.instance) return;

    this.instance.on('slideChangeTransitionEnd', (swiper) => {
      this.onSlideChangeTransitionEnd(swiper);
    });

    this.instance.on('update', () => {
      this.unbindLightboxEvents();
      this.bindLightboxEvents();
      // Re-initialize lightbox gallery connection after update
      this.initLightboxGallery();
    });

    this.bindLightboxEvents();
  }

  bindLightboxEvents() {
    if (this.lightboxGalleryEl && this.instance) {
      this.instance.slides.forEach((slide) => {
        // Use bound function reference for proper removal
        if (!slide._lightboxClickHandler) {
          slide._lightboxClickHandler = (e) => this.onSlideClick(e);
          slide.addEventListener('click', slide._lightboxClickHandler);
        }
      });
    }
  }

  unbindLightboxEvents() {
    if (this.lightboxGalleryEl && this.instance) {
      this.instance.slides.forEach((slide) => {
        if (slide._lightboxClickHandler) {
          slide.removeEventListener('click', slide._lightboxClickHandler);
          slide._lightboxClickHandler = null;
        }
      });
    }
  }

  onSlideChangeTransitionEnd(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) {
      return;
    }

    const currentThumbnailId = parseInt(activeSlide.dataset?.thumbnailId);
    const variantSelectorEl = this.sectionContainerEl?.querySelector('[variant-select]');

    const galleryVideos = this.host.querySelectorAll('video');
    if (galleryVideos) {
      galleryVideos.forEach(v => {
        if (v !== activeSlide.querySelector('video')) {
          v.pause();
        }
      });
    }

    const activeVideo = activeSlide.querySelector('video');
    if (activeVideo) {
      const playPromise = activeVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Ignore autoplay/playback policy errors; controls remain available.
        });
      }
    }

    // Dimension/360 slide borrows no real product media — never sync fabric/variant from it
    if (activeSlide.hasAttribute('gallery-media-dimension')) {
      return;
    }

    // Only auto-select variant from gallery image on templates where each image maps to a
    // variant (furniture / custom rugs). On rugs/shared media libraries, other colors'
    // featured images sit in the same gallery — syncing would swap the variant mid-browse
    // and make thumbnail clicks look like they open the "wrong" image.
    const isCustomRugs = !!document.querySelector('[custom-rugs]');
    const allowThumbnailVariantSync =
      isCustomRugs || this.templateSuffix === 'furniture_new';

    // Handle variant selection based on thumbnail
    // Match master/live behavior: when gallery image changes, auto-select corresponding variant
    if (this._ignoreNextSlideVariantSync) {
      this._ignoreNextSlideVariantSync = false;
      return;
    }
    if (allowThumbnailVariantSync && currentThumbnailId) {
      // Try variant-picker first (preferred method)
      const variantPicker = /** @type {any} */ (this.sectionContainerEl?.querySelector('variant-picker'));
      if (variantPicker && variantPicker.variants) {
        // Find all variants that have this thumbnail as their featured_media
        const thumbnailMatches = variantPicker.variants.filter(variant =>
          variant.featured_media?.id == currentThumbnailId
        );

        if (thumbnailMatches?.length) {
          // Find the best matching variant
          // Priority: 1. Variant that matches current options (except the changed option)
          //           2. First variant with this thumbnail
          let matchingVariant = null;

          if (variantPicker.currentOptions && variantPicker.currentOptions.length > 0) {
            // Try to find variant that matches current options as much as possible
            matchingVariant = thumbnailMatches.find(variant => {
              // Check if variant matches current options (allowing one option to differ)
              const matchingOptions = variant.options.filter((option, index) => 
                variantPicker.currentOptions[index] === option
              );
              // If most options match, this is likely the right variant
              return matchingOptions.length >= variant.options.length - 1;
            });
          }

          // Fallback: use first matching variant
          if (!matchingVariant) {
            matchingVariant = thumbnailMatches[0];
          }

          // Only update if variant is different from current
          if (matchingVariant && matchingVariant.id !== variantPicker.currentVariant?.id) {
            // Programmatically select the variant by clicking the corresponding radio inputs
            // This matches the behavior of clickFirstAvailableVariant in variant-picker.js
            const targetOptions = matchingVariant.options;
            const fieldsets = Array.from(variantPicker.fieldsets || []);

            // Check if we need to change any options
            let needsUpdate = false;
            const inputsToCheck = [];

            for (let i = 0; i < targetOptions.length && i < fieldsets.length; i++) {
              const optionValue = targetOptions[i];
              const fieldset = fieldsets[i];
              
              // Find radio input with matching value
              const radioInput = /** @type {HTMLInputElement | null} */ (
                fieldset.querySelector(`input[type="radio"][value="${CSS.escape(optionValue)}"]`)
              );
              
              if (radioInput && !radioInput.checked) {
                needsUpdate = true;
                inputsToCheck.push(radioInput);
              }
            }

            // If variant options need to change, click the first unchecked input
            // This will trigger onVariantChange which updates the variant
            if (needsUpdate && inputsToCheck.length > 0) {
              // Click the first unchecked input to trigger variant selection
              inputsToCheck[0].click();
            } else if (matchingVariant.id !== variantPicker.currentVariant?.id) {
              // Variant options are already set but variant hasn't updated
              // Force update by clicking first option of the matching variant
              if (fieldsets.length > 0 && targetOptions.length > 0) {
                const firstFieldset = fieldsets[0];
                const firstOptionValue = targetOptions[0];
                const firstInput = /** @type {HTMLInputElement | null} */ (
                  firstFieldset.querySelector(`input[type="radio"][value="${CSS.escape(firstOptionValue)}"]`)
                );
                if (firstInput) {
                  firstInput.click();
                }
              }
            }
          }
        }
      }
    }
  }

  onSlideClick(e) {
    // Prevent default if needed (e.g., if slide is inside a link)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Get the clicked slide and its data attributes
    const clickedSlide = e?.target?.closest('.swiper-slide');
    let thumbnailId = null;
    let slideIndex = null;
    
    if (clickedSlide && this.instance) {
      // Get thumbnail ID or slide index from the clicked slide
      thumbnailId = clickedSlide.getAttribute('data-thumbnail-id');
      slideIndex = Array.from(this.instance.slides).indexOf(clickedSlide);
    }
    
    // Fallback to current active slide if clicked slide not found
    if (!thumbnailId && this.instance) {
      const activeSlide = this.instance.slides[this.instance.activeIndex];
      if (activeSlide) {
        thumbnailId = activeSlide.getAttribute('data-thumbnail-id');
        slideIndex = this.instance.activeIndex;
      }
    }

    // CRITICAL FIX: Apply pending lightbox media before opening (if not already applied)
    if (this._pendingLightboxMedia && !this._pendingLightboxMediaApplied) {
      this.applyPendingLightboxMedia();
      this._pendingLightboxMediaApplied = true;
    }

    // Open lightbox if available - match TypeScript master: this.lightboxGallery?.open()
    if (this.lightboxGalleryEl) {
      // Try to get LightboxGallery instance
      const lightboxInstance = this.lightboxGalleryEl.lightboxGalleryInstance;
      if (lightboxInstance && typeof lightboxInstance.open === 'function') {
        // Sync slide before opening
        this.syncLightboxSlide(lightboxInstance, thumbnailId, slideIndex);
        lightboxInstance.open();
      } else {
        // Fallback: try to initialize lightbox if not already done
        if (window.LightboxGallery) {
          this.lightboxGalleryEl.lightboxGalleryInstance = new window.LightboxGallery(this.lightboxGalleryEl);
          if (this.lightboxGalleryEl.lightboxGalleryInstance.open) {
            // Sync slide index after a short delay to ensure instance is ready
            setTimeout(() => {
              this.syncLightboxSlide(this.lightboxGalleryEl.lightboxGalleryInstance, thumbnailId, slideIndex);
            }, 50);
            this.lightboxGalleryEl.lightboxGalleryInstance.open();
          }
        }
      }
    }
  }

  syncLightboxSlide(lightboxInstance, thumbnailId, slideIndex) {
    if (!lightboxInstance || !lightboxInstance.instance) return;

    const lightboxSwiper = lightboxInstance.instance;
    
    // If galleries are synced via controller, they'll sync automatically
    if (lightboxSwiper.controller?.control) {
      return;
    }

    // Find matching slide in lightbox by thumbnail ID (preferred method)
    if (thumbnailId && lightboxSwiper.slides) {
      const matchingSlideIndex = Array.from(lightboxSwiper.slides).findIndex((slide) => {
        return slide.getAttribute('data-thumbnail-id') === thumbnailId;
      });
      
      if (matchingSlideIndex !== -1) {
        lightboxSwiper.slideTo(matchingSlideIndex, 0);
        return;
      }
    }

    // Fallback: use slide index if thumbnail ID not found
    if (slideIndex !== null && slideIndex >= 0 && lightboxSwiper.slides && slideIndex < lightboxSwiper.slides.length) {
      lightboxSwiper.slideTo(slideIndex, 0);
    }
  }

  initSwiper() {
    const run = () => {
    try {
      const selected = this.host.querySelector('[data-selected]');
      const initialSlide = selected?.hasAttribute('data-selected') ?
        parseInt(selected.getAttribute('data-slide-index')) : 0;

      // Match TypeScript exactly - use global selectors (not scoped to this.host)
      // For Swiper v12 CDN bundle (swiper-bundle.min.js), all modules are auto-included
      // No need to specify modules array when using bundle
      const thumbs = new Swiper('.swiper--thumbs', {
        spaceBetween: 8,
        initialSlide, // Match TypeScript: thumbs initialSlide matches main initialSlide
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        watchOverflow: true,
        threshold: 12,
        navigation: {
          // Thumbs strip only — main gallery arrows use data-custom-nav + custom.js
          nextEl: '.product-gallery__thumbs .swiper-nav--next',
          prevEl: '.product-gallery__thumbs .swiper-nav--prev',
        },
        breakpoints: {
          960: {
            direction: 'vertical',
            allowTouchMove: true,
            slidesPerView: 6
          }
        }
      });

      // Match TypeScript exactly - use global selector and store as this.instance
      this.instance = new Swiper('.swiper--main', {
        spaceBetween: 10,
        slidesPerView: 1,
        initialSlide,
        allowTouchMove: false,
        observer: true,
        threshold: 12,
        touchStartForcePreventDefault: false,
        watchSlidesProgress: true,
        thumbs: {
          swiper: thumbs,
        }
      });
      
      // Match TypeScript: No custom navigation handlers needed
      // Swiper thumbs module handles navigation automatically via global selectors

    } catch (err) {
      console.error("[ProductGallery] ERROR in initSwiper:", err);
    } finally {
      this.host.classList.remove('product-gallery--idle');
      this.finishInit();
    }
    };

    if (typeof window.whenSwiperReady === 'function') {
      window.whenSwiperReady(run);
    } else if (typeof Swiper !== 'undefined') {
      run();
    } else {
      this.host.classList.remove('product-gallery--idle');
    }
  }

  syncGalleries() {
    if (this.lightboxGallery?.instance && this.instance) {
      if (this.lightboxGallery.instance.controller) {
        this.lightboxGallery.instance.controller.control = this.instance;
      }
      if (this.instance.controller) {
        this.instance.controller.control = this.lightboxGallery.instance;
      }
    }
  }

  updateShownVariantMedia(variantId) {
    // Size/color change already chose the variant — don't let the slide
    // transition click a different color radio (e.g. Dark Brown).
    this._ignoreNextSlideVariantSync = true;

    // CRITICAL FIX: Prevent double calls from variant-picker
    // variant-picker calls this twice on page load:
    //   1. From clickFirstAvailableVariant()
    //   2. From updatePDP()
    // This causes slides to be cleared twice, hiding the gallery
    if (this._lastProcessedVariantId === variantId) {
      return;
    }
    
    // Match TypeScript master exactly: product-gallery.directive.ts line 269-286
    const mediaEntry = Object.entries(this.data.variantGalleries).find(([key]) => +key === variantId);
    const newMedia = mediaEntry?.[1] || null;
    
    // Store processed variant ID BEFORE clearing slides
    // This prevents double processing if called again before slides are re-added
    this._lastProcessedVariantId = variantId;

    // If no media found and default gallery cards exist, don't change anything (TypeScript master line 273-275)
    if (!newMedia && this.instance?.el?.querySelectorAll('[gallery-media-default]').length) {
      return;
    }
    
    // Regression: Same gallery is already in the DOM (Liquid SSR). Rebuilding would drop the LCP image.
    if (newMedia?.length > 0 && this.currentSlidesMatchMedia(newMedia)) {
      this._lastProcessedVariantId = variantId;
      return;
    }

    // Get current variant to find featured_media
    const currentVariant = this.getCurrentVariantFromPicker(variantId);
    let featuredMediaId = null;
    
    if (currentVariant) {
      featuredMediaId = currentVariant.featured_media?.id;
    } else {
      // Fallback: try to get from variants data
      const variantData = this.data?.variants?.find((/** @type {any} */ v) => v.id === variantId);
      if (variantData) {
        featuredMediaId = variantData.featured_media?.id;
      }
    }

    // Disable and clear swipers (TypeScript master line 277)
    this.disableAndClearSwipers();

    // If variant has media, create new slides dynamically (TypeScript master line 279-280)
    if (newMedia?.length > 0) {
      this.updateWithNewMedia(newMedia);
    } else {
      // For furniture_new: Add all default slides, but custom.js will handle hide/show
      // custom.js's updateGalleryWithMetafield will show only relevant slides for current variant
      // For other templates: Revert to default media (TypeScript master line 282)
      this.revertToDefaultMedia();
    }

    // Enable and update swipers (TypeScript master line 285)
    // This is called AFTER dimension slide is added in updateWithNewMedia
    this.enableAndUpdateSwipers();

    // When the variantGalleries entry is empty but the variant has a featured_media,
    // revertToDefaultMedia() was called and swipers reset to slide 0.
    // Scroll to the slide that matches the variant's featured_media so the correct
    // image is shown instead of the first default slide.
    if (!(newMedia?.length > 0) && featuredMediaId && this.instance) {
      const slides = Array.from(this.instance.slides);
      const targetIndex = slides.findIndex(
        slide => slide.getAttribute('data-thumbnail-id') == featuredMediaId
      );
      if (targetIndex > 0) {
        this.instance.slideTo(targetIndex, 0, false);
        if (this.instance.thumbs?.swiper) {
          this.instance.thumbs.swiper.slideTo(targetIndex, 0, false);
        }
      }
    }
  }

  // Helper to get current variant from variant-picker
  getCurrentVariantFromPicker(variantId) {
    // Try to get variant from variant-picker custom element
    const variantPicker = /** @type {any} */ (document.querySelector('variant-picker'));
    if (variantPicker && variantPicker.currentVariant) {
      return variantPicker.currentVariant;
    }

    // Fallback: try to get from variant data JSON
    const variantDataScript = document.querySelector('#product-options-variant-data');
    if (variantDataScript) {
      try {
        const data = JSON.parse(variantDataScript.textContent);
        return data.variants?.find((/** @type {any} */ v) => v.id === variantId);
      } catch (e) {
        console.error('Error parsing variant data:', e);
      }
    }

    return null;
  }

  disableAndClearSwipers() {
    if (!this.instance) return;

    // CRITICAL FIX: If slides are already cleared (length = 0), skip
    // This prevents double clearing when updateShownVariantMedia() is called twice
    if (this.instance.slides.length === 0) {
      return;
    }
    
    this.instance.disable();
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.disable();
      this.instance.thumbs.swiper.removeAllSlides();
    }
    this.instance.removeAllSlides();
    this.instance.update();
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.update();
    }
  }

  revertToDefaultMedia() {
    if (!this.instance) return;

    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.appendSlide(this.defaultThumbs);
    }
    this.instance.appendSlide(this.defaultSlides);
    this.resetLightboxGallery();
  }


  updateWithNewMedia(newMedia) {
    if (!this.instance) return;

    const slides = this.SlideElements(newMedia);
    const thumbs = this.SlideElements(newMedia, "thumb");

    // Convert HTML strings to DOM elements
    const slideElements = slides.map(html => this.htmlToElement(html));
    const thumbElements = thumbs.map(html => this.htmlToElement(html));

    this.instance.appendSlide(slideElements);
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.appendSlide(thumbElements);
    }
    
    // Update swipers after adding variant media slides (BEFORE adding dimension slide)
    // Match TypeScript: product-gallery.directive.ts line 312-313
    this.instance.update();
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.update();
    }

    // Add dimension slide if exists (AFTER update calls)
    // Match TypeScript: product-gallery.directive.ts line 315-326
    if (this.dimensionSlide && this.dimensionThumb) {
      const insertIndex = (this.templateSuffix === 'furniture_new') ? 2 : 1;

      const clonedDimensionSlide = this.dimensionSlide.cloneNode(true);
      const clonedDimensionThumb = this.dimensionThumb.cloneNode(true);
      
      // Ensure dimension slide is visible (remove display:none if present)
      if (clonedDimensionSlide instanceof HTMLElement) {
        clonedDimensionSlide.style.display = '';
      }
      if (clonedDimensionThumb instanceof HTMLElement) {
        clonedDimensionThumb.style.display = '';
      }
      
      this.instance.addSlide(
        insertIndex,
        clonedDimensionSlide
      );
      if (this.instance.thumbs?.swiper) {
        this.instance.thumbs.swiper.addSlide(
          insertIndex,
          clonedDimensionThumb
        );
      }
    }

    this.updateLightboxGallery(newMedia);
  }

  enableAndUpdateSwipers() {
    if (!this.instance) return;

    // Enable first so slideTo and update() work correctly
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.enable();
    }
    this.instance.enable();

    // Reset to slide 0 BEFORE calling update(), so the thumbs module
    // reads activeIndex=0 (not the stale index left over from the previous variant).
    // slideTo with runCallbacks=false does NOT fire events, so update() must follow.
    this.instance.slideTo(0, 0, false);
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.slideTo(0, 0, false);
    }

    // update() fires the 'update' event; the thumbs module handles it and
    // applies swiper-slide-thumb-active to the slide at activeIndex=0
    this.instance.update();
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.update();
    }

    this.dispatchGalleryUpdated();
  }

  dispatchGalleryUpdated() {
    this.host.dispatchEvent(new CustomEvent('product-gallery:updated', { bubbles: true }));
  }

  updateLightboxGallery(newMedia) {
    if (!this.lightboxGallery?.instance) {
      // Lightbox not ready yet - store pending media to apply later (on first click)
      this._pendingLightboxMedia = newMedia;
      this._pendingLightboxMediaApplied = false;
      return;
    }

    this.unbindLightboxEvents();
    this.disableAndClearLightboxSwipers();

    const slides = this.SlideElements(newMedia, 'lightbox');
    const thumbs = this.SlideElements(newMedia, 'lightbox-thumb');

    // Convert HTML strings to DOM elements
    const slideElements = slides.map(html => this.htmlToElement(html));
    const thumbElements = thumbs.map(html => this.htmlToElement(html));

    if (this.lightboxGallery.instance.thumbs?.swiper) {
      this.lightboxGallery.instance.thumbs.swiper.appendSlide(thumbElements);
    }
    this.lightboxGallery.instance.appendSlide(slideElements);

    if (this.dimensionSlide && this.dimensionThumb) {
      const insertIndex = (this.templateSuffix === 'furniture_new') ? 2 : 1;

      this.lightboxGallery.instance.addSlide(
        insertIndex,
        this.dimensionSlide
      );
      if (this.lightboxGallery.instance.thumbs?.swiper) {
        this.lightboxGallery.instance.thumbs.swiper.addSlide(
          insertIndex,
          this.dimensionThumb
        );
      }
    }

    this.enableAndUpdateLightboxSwipers();
    // Mark as applied since we just updated directly
    this._pendingLightboxMediaApplied = true;
  }

  resetLightboxGallery() {
    if (!this.lightboxGallery?.instance) {
      return;
    }

    this.unbindLightboxEvents();
    this.disableAndClearLightboxSwipers();

    this.lightboxGallery.instance.appendSlide(this.defaultLightboxSlides);
    if (this.lightboxGallery.instance.thumbs?.swiper) {
      this.lightboxGallery.instance.thumbs.swiper.appendSlide(this.defaultLightboxThumbs);
    }

    this.enableAndUpdateLightboxSwipers();
  }

  disableAndClearLightboxSwipers() {
    if (!this.lightboxGallery?.instance) return;

    this.lightboxGallery.instance.disable?.();
    if (this.lightboxGallery.instance.thumbs?.swiper) {
      this.lightboxGallery.instance.thumbs.swiper.disable?.();
      this.lightboxGallery.instance.thumbs.swiper.removeAllSlides?.();
    }
    this.lightboxGallery.instance.removeAllSlides?.();
    this.lightboxGallery.instance.update?.();
  }

  enableAndUpdateLightboxSwipers() {
    if (!this.lightboxGallery?.instance) return;

    this.lightboxGallery.instance.update?.();
    if (this.lightboxGallery.instance.thumbs?.swiper) {
      this.lightboxGallery.instance.thumbs.swiper.update?.();
      this.lightboxGallery.instance.thumbs.swiper.enable?.();
    }
    this.lightboxGallery.instance.enable?.();
  }

  // Match TypeScript SlideElements method exactly (lines 343-402)
  SlideElements(mediaList, type = "main-gallery") {
    return mediaList.map((media, index) => {
      const mediaType = media.media_type || 'image';
      const isThumbType = type === "thumb" || type === "lightbox-thumb";
      const mediaThumbUrl = media.preview_image || media.url;

      if ((mediaType === 'video' || mediaType === 'external_video') && isThumbType) {
        const thumbImg = mediaThumbUrl ? `
          <img
            src="${mediaThumbUrl}"
            class="inlineImg"
            style="--focal-x: 50%; --focal-y: 50%; width: 100%; aspect-ratio: 1;"
            loading="lazy"
            decoding="async"
            alt="${this.escapeHtml(media.alt || '')}"
          />
        ` : `
          <div class="product-gallery__video-fallback" aria-label="Video thumbnail">
            <span>Video</span>
          </div>
        `;

        if (type === "thumb") {
          return `
            <div class="swiper-slide" style="min-height: 64px; aspect-ratio: 1;">
              <div class="product-gallery__card">
                ${thumbImg}
              </div>
            </div>
          `;
        }

        return `
          <div class="swiper-slide">
            ${thumbImg}
          </div>
        `;
      }

      if (mediaType === 'video' && !isThumbType) {
        const videoSrc = media.video_mp4_url || '';
        const videoTag = videoSrc ? `
          <video controls playsinline preload="metadata">
            <source src="${videoSrc}" type="video/mp4" />
          </video>
        ` : `
          <div class="product-gallery__video-fallback" aria-label="Video media">
            <span>Video unavailable</span>
          </div>
        `;

        return `
          <div class="swiper-slide" data-thumbnail-id="${media.id}" style="width: 100%;">
            <div class="product-gallery__card">
              ${videoTag}
            </div>
          </div>
        `;
      }

      if (mediaType === 'external_video' && !isThumbType) {
        const embedUrl = media.external_video_embed_url || '';
        const iframe = embedUrl ? `
          <iframe
            src="${embedUrl}"
            title="${this.escapeHtml(media.alt || 'Product video')}"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        ` : `
          <div class="product-gallery__video-fallback" aria-label="External video media">
            <span>Video unavailable</span>
          </div>
        `;

        return `
          <div class="swiper-slide" data-thumbnail-id="${media.id}" style="width: 100%;">
            <div class="product-gallery__card">
              ${iframe}
            </div>
          </div>
        `;
      }

      // TypeScript line 345: Replace query parameters and handle _16x16. pattern
      let mediaUrl = media.url || media.preview_image || '';

      // Handle query parameter format: width=16/100&height=16/100 -> dynamic placeholders
      if (mediaUrl.includes('width=16') || mediaUrl.includes('height=16') || mediaUrl.includes('width=100') || mediaUrl.includes('height=100')) {
        mediaUrl = mediaUrl
          .replace('width=16', 'width={width}')
          .replace('height=16', 'height={height}')
          .replace('width=100', 'width={width}')
          .replace('height=100', 'height={height}');
      }
      // Handle _16x16. / _100x100. pattern: -> _{width}x{height}.
      else if (mediaUrl.includes('_16x16.') || mediaUrl.includes('_100x100.')) {
        mediaUrl = mediaUrl
          .replace('_16x16.', '_{width}x{height}.')
          .replace('_100x100.', '_{width}x{height}.');
      }

      // Generate data-srcset directly for immediate display (like live site)
      const widths = [540, 720, 900, 1080, 1250, 1440, 1680, 1920];
      const aspectRatio = media.aspect_ratio || 1;
      const srcsetParts = widths.map(width => {
        const height = Math.round(width / aspectRatio);
        let srcsetUrl = mediaUrl
          .replace(/\{width\}/g, width)
          .replace(/\{height\}/g, height);
        return `${srcsetUrl} ${width}w`;
      });
      const dataSrcset = srcsetParts.join(', ');

      // Generate proper src for immediate display (720px width for faster initial load)
      const initialWidth = 720;
      const initialHeight = Math.round(initialWidth / aspectRatio);
      let srcUrl = mediaUrl
        .replace(/\{width\}/g, initialWidth)
        .replace(/\{height\}/g, initialHeight);

      // Use native browser srcset with auto sizes - CSS handles layout constraints
      const sizesValue = type === 'thumb' ? '62px' : '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px';

      //loading strategy eager and fetchpriority high for the first index image
      //lazy and low fetchpriority for the other images
      const loadingStrategy = index === 0 ? 'eager' : 'lazy';
      const fetchpriority = index === 0 ? 'high' : 'low';
      const decoding = index === 0 ? 'async' : 'sync';
      // thumb loading strategy eager and fetchpriority high for the first 6 images
      // lazy and low fetchpriority for the other images
      const thumbLoadingStrategy = index < 6 ? 'eager' : 'lazy';
      const thumbFetchpriority = index < 6 ? 'high' : 'low';
      const thumbDecoding = index < 6 ? 'async' : 'sync';
      const Img = `
        <img
          src="${srcUrl}"
          srcset="${dataSrcset}"
          sizes="${sizesValue}"
          class="inlineImg"
          style="
            --focal-x: 50%;
            --focal-y: 50%;
            ${["thumb", "lightbox", "main-gallery"].includes(type) ? "width: 100%;" : ""}
            aspect-ratio: 1;
          "
          loading="${loadingStrategy}"
          fetchpriority="${fetchpriority}"
          decoding="${decoding}"
          width="100%"
          height="100%"
          data-aspectratio="${media.aspect_ratio}"
          alt="${this.escapeHtml(media.alt || '')}"
        />
      `;

      const thumbWidths = [62];
      const thumbAspectRatio = media.aspect_ratio || 1;
      const thumbSrcsetParts = thumbWidths.map(width => {
        const height = Math.round(width / thumbAspectRatio);
        let thumbSrcsetUrl = mediaUrl
          .replace(/\{width\}/g, width)
          .replace(/\{height\}/g, height);
        return `${thumbSrcsetUrl} ${width}w`;
      });
      const dataThumbSrcset = thumbSrcsetParts.join(', ');

      const newThumbImg = `
        <img
          src="${srcUrl}"
          srcset="${dataThumbSrcset}"
          sizes="${sizesValue}"
          loading="${thumbLoadingStrategy}"
          fetchpriority="${thumbFetchpriority}"
          decoding="${thumbDecoding}"
          class="inlineImg thumbnail-image" alt="${this.escapeHtml(media.alt || '')}"
          style="--focal-x: 50%; --focal-y: 50%; width: 100%; aspect-ratio: 1;"
        />
      `;
      if (type === "thumb") {
        return `
          <div class="swiper-slide" data-thumbnail-id="${media.id}" style="min-height: 64px; aspect-ratio: 1;">
            <div class="product-gallery__card">
              ${newThumbImg}
            </div>
          </div>
        `;
      }

      if (type === "lightbox") {
        return `
          <div class="swiper-slide" data-thumbnail-id="${media.id}">
            <div class="swiper-zoom-container">
              ${Img}
            </div>
          </div>
        `;
      }

      if (type === "lightbox-thumb") {
        return `
          <div class="swiper-slide" data-thumbnail-id="${media.id}">
            ${Img}
          </div>
        `;
      }

      // main-gallery (default) - TypeScript lines 392-400
      return `
        <div class="swiper-slide" data-thumbnail-id="${media.id}" style="width: 100%;">
          <div class="product-gallery__card">
            <div class="swiper-zoom-container">
              ${Img}
            </div>
          </div>
        </div>
      `;
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Convert HTML string to DOM element
  htmlToElement(html) {
    const template = document.createElement('template');
    html = html.trim();
    template.innerHTML = html;
    return template.content.firstChild;
  }
}

// Initialize product galleries - Swiper should already be loaded by now
function initializeProductGalleries() {
  const productGalleries = document.querySelectorAll('[product-gallery]');

  if (productGalleries.length === 0) {
    return;
  }

  productGalleries.forEach((gallery) => {
    if (!gallery.productGalleryInstance) {
      gallery.productGalleryInstance = new ProductGallery(gallery);
    }
  });
}

function startInitialization() {
  const run = () => initializeProductGalleries();

  if (typeof window.whenSwiperReady === 'function') {
    window.whenSwiperReady(run).catch(() => {
      document.querySelectorAll('[product-gallery]').forEach((gallery) => {
        gallery.classList.remove('product-gallery--idle');
      });
    });
    return;
  }

  if (typeof Swiper !== 'undefined') {
    run();
    return;
  }

  document.querySelectorAll('[product-gallery]').forEach((gallery) => {
    gallery.classList.remove('product-gallery--idle');
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startInitialization);
} else {
  // DOM already ready, start initialization
  startInitialization();
}

// Listen for variant changes to update gallery
document.addEventListener('variant:change', function (event) {
  const variantId = event.detail?.variantId || event.detail?.variant?.id;
  if (variantId) {
    const productGalleries = document.querySelectorAll('[product-gallery]');
    productGalleries.forEach(gallery => {
      if (gallery.productGalleryInstance) {
        gallery.productGalleryInstance.updateShownVariantMedia(variantId);
      }
    });
  }
});

// Handle variant selector changes (supports both custom rugs and regular products)
// Matches TypeScript variant-select.directive.ts behavior
function handleVariantSelectorChange(e) {
  // Check if this is a custom rugs flow
  const isCustomRugs = !!document.querySelector('[custom-rugs]');
  const isPDP = document.body.classList.contains('template-product');
  let variantId = null;

  if (isCustomRugs) {
    // For custom rugs: Color fieldset value IS the variant ID (TypeScript variant-select.directive.ts line 308-310)
    // Get the variant-select container
    const variantSelectContainer = document.querySelector('[variant-select]');
    if (variantSelectContainer) {
      // Find Color fieldset - try multiple selectors for compatibility
      const colorFieldset = variantSelectContainer.querySelector('fieldset#CustomRug-color') ||
        variantSelectContainer.querySelector('fieldset[id*="CustomRug-color"]') ||
        Array.from(variantSelectContainer.querySelectorAll('fieldset')).find(fs =>
          fs.querySelector('[cylindo-variant-selector="Color"]')
        );

      if (colorFieldset) {
        const colorInput = colorFieldset.querySelector('input[type="radio"]:checked');
        if (colorInput && colorInput.value) {
          variantId = parseInt(colorInput.value);
        }
      }
    }

    // For custom rugs: Also dispatch variant:changed event for gallery update
    // This works alongside variant-picker for dual functionality
    if (variantId) {
      const variantChangedEvent = new CustomEvent('variant:changed', {
        detail: {
          variantId: variantId,
          variant: { id: variantId }
        },
        bubbles: true
      });
      document.dispatchEvent(variantChangedEvent);
      return; // Don't dispatch variant:change for custom rugs
    }
  }

  // For regular products: Check if variant-picker exists
  // If it does, let it handle URL but we still need to handle gallery for fallback
  const variantPicker = document.querySelector('variant-picker');
  if (variantPicker && !isCustomRugs) {
    // variant-picker handles everything for regular products
    return;
  }

  // Fallback for regular products without variant-picker
  if (!isCustomRugs) {
    const variantSelectContainer = e.target.closest('[variant-select]');
    if (variantSelectContainer) {
      const checkedInput = variantSelectContainer.querySelector('input:checked, select');
      if (checkedInput) {
        variantId = parseInt(checkedInput.value) || parseInt(checkedInput.getAttribute('data-variant-id'));
      }
    }
  }

  // Dispatch variant:change event (matches TypeScript behavior)
  // Gallery will be updated by the variant:change event listener
  if (variantId) {
    const variantChangeEvent = new CustomEvent('variant:change', {
      detail: {
        variantId: variantId,
        variant: { id: variantId }
      },
      bubbles: true
    });
    document.dispatchEvent(variantChangeEvent);
  }
}

// Listen to variant-select changes (works for both initial load and dynamic content)
function setupVariantSelectListeners() {
  const variantSelectors = document.querySelectorAll('[variant-select]');
  variantSelectors.forEach(selector => {
    // Remove existing listener if any (prevent duplicates)
    selector.removeEventListener('change', handleVariantSelectorChange);
    selector.addEventListener('change', handleVariantSelectorChange);
  });
}

// Setup listeners on initial load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupVariantSelectListeners);
} else {
  setupVariantSelectListeners();
}

// Also listen for fieldset changes in custom rugs (Color fieldset changes)
function setupCustomRugsListeners() {
  const customRugsContainer = document.querySelector('[custom-rugs]');
  if (customRugsContainer) {
    const variantSelectContainer = document.querySelector('[variant-select]');
    if (variantSelectContainer) {
      // Find Color fieldset - try multiple selectors
      const colorFieldset = variantSelectContainer.querySelector('fieldset#CustomRug-color') ||
        variantSelectContainer.querySelector('fieldset[id*="CustomRug-color"]') ||
        Array.from(variantSelectContainer.querySelectorAll('fieldset')).find(fs =>
          fs.querySelector('[cylindo-variant-selector="Color"]')
        );

      if (colorFieldset) {
        colorFieldset.removeEventListener('change', handleVariantSelectorChange);
        colorFieldset.addEventListener('change', handleVariantSelectorChange);
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCustomRugsListeners);
} else {
  setupCustomRugsListeners();
}

// Re-setup listeners when content changes (e.g., section reloads)
document.addEventListener('shopify:section:load', () => {
  setupVariantSelectListeners();
  setupCustomRugsListeners();
});

// Helper function to update gallery from external code
window.updateProductGallery = function (variantId) {
  const productGalleries = document.querySelectorAll('[product-gallery]');
  productGalleries.forEach(gallery => {
    if (gallery.productGalleryInstance) {
      gallery.productGalleryInstance.updateShownVariantMedia(variantId);
    }
  });
};

// Listen for variant changes from variant-picker
document.addEventListener('variant:changed', (event) => {
  const { variantId } = event.detail;
  if (!variantId) {
    return;
  }
  
  // Find all galleries and update them
  const galleries = document.querySelectorAll('[product-gallery]');
  galleries.forEach((galleryEl) => {
    if (galleryEl.productGalleryInstance && galleryEl.productGalleryInstance.updateShownVariantMedia) {
      galleryEl.productGalleryInstance.updateShownVariantMedia(variantId);
    }
  });
});

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.ProductGallery = ProductGallery;
}